/**
 * CodeMirror 6 编辑器封装
 * =======================
 * 打包后挂到 window.CM6Editor，供页面直接使用（零 CDN）。
 *
 * 能力：
 *   - Python 语法高亮（@codemirror/lang-python）
 *   - 智能补全：turtle API（带中文说明）、Python 内置、当前文件符号、模块成员、代码片段
 *   - 实时语法检查：外部注入异步 checker，错误画波浪线、悬停提示、F8 跳转
 *   - 缩进参考线（自研 Decoration）
 *   - 字号 12~22px 可调、明暗主题
 *   - 全屏、查找替换、括号匹配、自动闭合
 *
 * 对外接口：
 *   var ed = CM6Editor.create(parentEl, { value, fontSize, theme, onChange, onCursor, check });
 *   ed.getValue() / ed.setValue() / ed.focus() / ed.destroy()
 *   ed.setFontSize(px) / ed.setTheme(name) / ed.runLint() / ed.getCursor() / ed.setCursor(offset)
 */

import { EditorState, Compartment, EditorSelection, RangeSet, RangeSetBuilder, StateEffect, StateField } from "@codemirror/state";
import {
  EditorView,
  keymap,
  lineNumbers,
  highlightActiveLineGutter,
  highlightSpecialChars,
  drawSelection,
  dropCursor,
  rectangularSelection,
  crosshairCursor,
  highlightActiveLine,
  Decoration,
  ViewPlugin,
  gutter,
  GutterMarker,
} from "@codemirror/view";
import {
  defaultHighlightStyle,
  syntaxHighlighting,
  indentOnInput,
  bracketMatching,
  foldGutter,
  foldKeymap,
  indentUnit,
} from "@codemirror/language";
import { defaultKeymap, history, historyKeymap, indentWithTab } from "@codemirror/commands";
import { searchKeymap, highlightSelectionMatches, search } from "@codemirror/search";
import {
  autocompletion,
  completionKeymap,
  closeBrackets,
  closeBracketsKeymap,
  CompletionContext,
} from "@codemirror/autocomplete";
import { linter, lintGutter, lintKeymap, openLintPanel, forceLinting } from "@codemirror/lint";
import { python } from "@codemirror/lang-python";

// ============================================================ 缩进参考线

const INDENT_SIZE = 4;

const guideDecoration = Decoration.mark({ class: "cm-indent-guide" });

const indentGuides = ViewPlugin.fromClass(
  class {
    constructor(view) {
      this.decorations = this.build(view);
    }
    update(update) {
      if (update.docChanged || update.viewportChanged || update.selectionSet) {
        this.decorations = this.build(update.view);
      }
    }
    build(view) {
      const builder = new RangeSetBuilder();
      const doc = view.state.doc;
      const { from, to } = view.viewport;
      // 收集后排序（RangeSetBuilder 要求按位置递增）
      const items = [];
      for (let pos = from; pos <= to; ) {
        const line = doc.lineAt(pos);
        const text = line.text;
        if (text.trim().length > 0) {
          let spaces = 0;
          for (let i = 0; i < text.length; i++) {
            if (text[i] === " ") spaces++;
            else if (text[i] === "\t") spaces += INDENT_SIZE;
            else break;
          }
          const levels = Math.floor(spaces / INDENT_SIZE);
          for (let lv = 0; lv < levels; lv++) {
            const start = line.from + lv * INDENT_SIZE;
            if (start + INDENT_SIZE <= line.to) {
              items.push([start, start + INDENT_SIZE]);
            }
          }
        }
        if (line.to >= to) break;
        pos = line.to + 1;
      }
      for (const [a, b] of items) builder.add(a, b, guideDecoration);
      return builder.finish();
    }
  },
  { decorations: (v) => v.decorations }
);

// ============================================================ 断点 / 当前执行行
//
// 断点：行号槽里点一下切换，红点用 GutterMarker 画（不占正文宽度）。
// 当前执行行：整行加 .cm-debug-line 背景，等价于原站那条黄底。

const setBreakpointsEffect = StateEffect.define();
const setDebugLineEffect = StateEffect.define();

const bpDot = new (class extends GutterMarker {
  toDOM() {
    const el = document.createElement("div");
    el.className = "cm-bp-dot";
    return el;
  }
})();

function buildBreakpointMarks(doc, lines) {
  const marks = [];
  for (const n of lines) {
    if (n >= 1 && n <= doc.lines) marks.push(bpDot.range(doc.line(n).from));
  }
  return RangeSet.of(marks, true);
}

const breakpointsField = StateField.define({
  create: () => ({ lines: [], marks: RangeSet.empty }),
  update(value, tr) {
    let lines = value.lines;
    let dirty = tr.docChanged;
    for (const e of tr.effects) {
      if (e.is(setBreakpointsEffect)) {
        lines = e.value.slice().sort((a, b) => a - b);
        dirty = true;
      }
    }
    if (!dirty) return value;
    return { lines, marks: buildBreakpointMarks(tr.state.doc, lines) };
  },
});

/** 断点槽：宽 14px，点一下切换。 */
function breakpointGutter(onToggle) {
  return gutter({
    class: "cm-bp-gutter",
    markers: (v) => v.state.field(breakpointsField).marks,
    initialSpacer: () => bpDot,
    domEventHandlers: {
      mousedown(view, line) {
        const lineNo = view.state.doc.lineAt(line.from).number;
        const cur = view.state.field(breakpointsField).lines;
        const next = cur.indexOf(lineNo) >= 0 ? cur.filter((n) => n !== lineNo) : cur.concat(lineNo);
        view.dispatch({ effects: setBreakpointsEffect.of(next) });
        if (onToggle) onToggle(next);
        return true;
      },
    },
  });
}

// ============================================================ 拖拽选代码时贴边自动滚动
//
// CM6 自带的自动滚动只在鼠标被拖到编辑器「外面」时才生效。
// 但学生拖着拖着，指针通常正好停在编辑器底边上（尤其是拖到一半想停一下），
// 这时一点都不滚 —— 表现就是「选不全，得松手、滚一下、再 Shift+点」。
//
// 这里补一个滚动热区：指针在可视区上下 44px 以内就持续滚动，
// 并在每次滚动后补一次选区扩展（滚动本身不会触发 mousemove，
// 不补的话选区会一直停在原位）。

const EDGE_ZONE = 44;
const EDGE_MAX_SPEED = 26;

const edgeDragScroll = () =>
  ViewPlugin.fromClass(
    class {
      constructor(view) {
        this.view = view;
        this.anchor = null;
        this.x = 0;
        this.y = 0;
        this.raf = 0;
        this.tick = this.tick.bind(this);
        this.onMove = this.onMove.bind(this);
        this.onUp = this.onUp.bind(this);
        this.onDown = (e) => {
          if (e.button !== 0) return;
          this.anchor = view.posAtCoords({ x: e.clientX, y: e.clientY });
          this.x = e.clientX;
          this.y = e.clientY;
          document.addEventListener("mousemove", this.onMove, true);
          document.addEventListener("mouseup", this.onUp, true);
        };
        view.contentDOM.addEventListener("mousedown", this.onDown);
      }

      onMove(e) {
        if (this.anchor == null) return;
        this.x = e.clientX;
        this.y = e.clientY;
        if (this.raf || !this.inZone()) return;
        this.raf = requestAnimationFrame(this.tick);
      }

      inZone() {
        const r = this.view.scrollDOM.getBoundingClientRect();
        if (this.y <= r.top || this.y >= r.bottom) return false; // 出去了交给 CM6 自己滚
        return this.y < r.top + EDGE_ZONE || this.y > r.bottom - EDGE_ZONE;
      }

      tick() {
        this.raf = 0;
        const view = this.view;
        if (this.anchor == null || !this.inZone()) return;

        const r = view.scrollDOM.getBoundingClientRect();
        const over =
          this.y < r.top + EDGE_ZONE
            ? (this.y - r.top) / EDGE_ZONE - 1 // -1 .. 0
            : (this.y - (r.bottom - EDGE_ZONE)) / EDGE_ZONE; // 0 .. 1
        const speed = Math.max(-1, Math.min(1, over)) * EDGE_MAX_SPEED;

        const el = view.scrollDOM;
        const before = el.scrollTop;
        el.scrollTop = before + speed;
        if (el.scrollTop === before) return; // 到顶/到底了

        // 滚动之后把「最后可见的那一行」补进选区
        const y = Math.max(r.top + 2, Math.min(this.y, r.bottom - 2));
        const pos = view.posAtCoords({ x: this.x, y });
        if (pos != null) {
          view.dispatch({ selection: { anchor: this.anchor, head: pos } });
        }
        this.raf = requestAnimationFrame(this.tick);
      }

      onUp() {
        this.anchor = null;
        if (this.raf) cancelAnimationFrame(this.raf);
        this.raf = 0;
        document.removeEventListener("mousemove", this.onMove, true);
        document.removeEventListener("mouseup", this.onUp, true);
      }

      destroy() {
        this.onUp();
        this.view.contentDOM.removeEventListener("mousedown", this.onDown);
      }
    }
  );

const debugLineDeco = Decoration.line({ class: "cm-debug-line" });

const debugLineField = StateField.define({
  create: () => Decoration.none,
  update(deco, tr) {
    deco = deco.map(tr.changes);
    for (const e of tr.effects) {
      if (e.is(setDebugLineEffect)) {
        if (!e.value) return Decoration.none;
        const doc = tr.state.doc;
        const n = Math.min(Math.max(1, e.value), doc.lines);
        return Decoration.set([debugLineDeco.range(doc.line(n).from)]);
      }
    }
    return deco;
  },
  provide: (f) => EditorView.decorations.from(f),
});

// ============================================================ 符号提取

/** 从文档里提取用户定义的变量 / 函数 / 类 / 循环变量，用于补全。 */
function extractSymbols(text) {
  const symbols = new Map();

  const add = (name, type, detail) => {
    if (!name || name.length < 1) return;
    if (symbols.has(name)) return;
    symbols.set(name, { label: name, type, detail });
  };

  const lines = text.split("\n");
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    let m;

    if ((m = line.match(/^\s*def\s+([A-Za-z_]\w*)/))) {
      add(m[1], "function", "你定义的函数");
      continue;
    }
    if ((m = line.match(/^\s*class\s+([A-Za-z_]\w*)/))) {
      add(m[1], "class", "你定义的类");
      continue;
    }
    if ((m = line.match(/^\s*for\s+([A-Za-z_]\w*)\s+in\b/))) {
      add(m[1], "variable", "循环变量");
      // for a, b in ...
      continue;
    }
    if ((m = line.match(/^\s*import\s+([\w.]+)(?:\s+as\s+(\w+))?/))) {
      add(m[2] || m[1].split(".")[0], "namespace", "导入的模块");
      continue;
    }
    if ((m = line.match(/^\s*from\s+([\w.]+)\s+import\s+(.+)/))) {
      m[2].split(",").forEach((part) => {
        const name = part.trim().split(/\s+as\s+/);
        add((name[1] || name[0]).trim(), "namespace", "从 " + m[1] + " 导入");
      });
      continue;
    }
    // 普通赋值（排除 ==、<=、>= 等）
    if ((m = line.match(/^\s*([A-Za-z_]\w*)\s*(?:,\s*([A-Za-z_]\w*)\s*)*=(?!=)/))) {
      const lhs = line.split("=")[0];
      lhs.split(",").forEach((part) => {
        const name = part.trim();
        if (/^[A-Za-z_]\w*$/.test(name)) add(name, "variable", "你定义的变量");
      });
    }
  }
  return [...symbols.values()];
}

/** 识别“某个变量是海龟对象”。 */
function findTurtleVars(text) {
  const vars = new Set();
  const re = /([A-Za-z_]\w*)\s*=\s*(?:turtle|t)\.(?:Turtle|Pen|RawTurtle|RawPen)\s*\(/g;
  let m;
  while ((m = re.exec(text))) vars.add(m[1]);
  return vars;
}

/** 识别“某个名字是已导入的模块”。 */
function findImportedModules(text) {
  const mods = new Set(["turtle", "math", "random", "time"]);
  let m;
  const re1 = /^\s*import\s+([\w.]+)(?:\s+as\s+(\w+))?/gm;
  while ((m = re1.exec(text))) mods.add(m[2] || m[1].split(".")[0]);
  const re2 = /^\s*from\s+([\w.]+)\s+import/gm;
  while ((m = re2.exec(text))) mods.add(m[1].split(".")[0]);
  return mods;
}

// ============================================================ 补全项渲染
//
// 视觉结构对齐 codemao 海龟编辑器（实测 DOM）：
//
//   <li>                                  ← 一行一条，flex 换行容器
//     <span.cm-completionLabel>forward</span>     第一行：名字（命中部分橙色）
//     <span.cm-completionDetail>(distance)</span> 第一行：签名，灰色
//     <div.cm-completionColorBlock>              颜色项才有的色块
//     <div.cm-completionHint>                    第二行：中文说明
//       <div.cm-completionDescription>…</div>    只在「选中 / 悬停」时显示
//     </div>
//   </li>
//
// CM6 的默认渲染只给 label + detail，这里用 addToOptions 追加色块与说明，
// 再用 CSS 让说明项占满第二行（flex-basis:100%）。

function colorBlockRenderer(completion) {
  if (!completion.color) return null;
  const el = document.createElement("span");
  el.className = "cm-completionColorBlock";
  el.style.backgroundColor = completion.color;
  return el;
}

function hintRenderer(completion) {
  if (!completion.desc) return null;
  const wrap = document.createElement("div");
  wrap.className = "cm-completionHint";
  const desc = document.createElement("div");
  desc.className = "cm-completionDescription";
  desc.textContent = completion.desc;
  wrap.appendChild(desc);
  return wrap;
}

const completionRenderers = {
  addToOptions: [
    { render: colorBlockRenderer, position: 60 },
    { render: hintRenderer, position: 90 },
  ],
};

// ============================================================ 补全源

/** 函数/方法：插入 `name()`，光标停在括号里。 */
function applyCall(label) {
  return (view, completion, from, to) => {
    const insert = label + "()";
    view.dispatch({
      changes: { from, to, insert },
      selection: { anchor: from + label.length + 1 },
    });
  };
}

/** 代码模板：把 $0 处作为光标落点。 */
function applySnippet(tpl) {
  return (view, completion, from, to) => {
    const idx = tpl.indexOf("$0");
    const insert = idx >= 0 ? tpl.slice(0, idx) + tpl.slice(idx + 2) : tpl;
    const cursor = idx >= 0 ? idx : insert.length;
    view.dispatch({
      changes: { from, to, insert },
      selection: { anchor: from + cursor },
    });
  };
}

/**
 * 把词表条目转成 CM6 补全项。
 * kind: "call" 插入 name() ；"name" 只插入名字（颜色、关键字）
 */
function toCompletions(list, type, kind) {
  return list.map((c) => {
    const item = {
      label: c.label,
      detail: c.detail || "",
      desc: c.desc || "",
      type: type,
    };
    if (c.color) item.color = c.color;
    if (kind === "call" && /^[A-Za-z_]\w*$/.test(c.label)) {
      item.apply = applyCall(c.label);
    }
    return item;
  });
}

/** 当前光标是否落在一个没闭合的引号里；返回引号字符或 null。 */
function openQuoteAt(state, pos) {
  const line = state.doc.lineAt(pos);
  const before = state.sliceDoc(line.from, pos);
  let quote = null;
  for (let i = 0; i < before.length; i++) {
    const ch = before[i];
    if (ch === "\\") {
      i++;
      continue;
    }
    if (quote) {
      if (ch === quote) quote = null;
    } else if (ch === '"' || ch === "'") {
      quote = ch;
    }
  }
  return quote;
}

/** 光标所在行是不是「设置颜色」这类函数的参数里。 */
function inColorCall(state, pos) {
  const line = state.doc.lineAt(pos);
  const before = state.sliceDoc(line.from, pos);
  return /\b(pencolor|fillcolor|color|bgcolor)\s*\(\s*["'][^"']*$/.test(before);
}

function pythonCompletions(context) {
  const data = window.PY_COMPLETIONS || {};
  const state = context.state;
  const text = state.doc.toString();

  // 1) 颜色：在 pencolor('… 这类参数的引号里
  if (inColorCall(state, context.pos) && openQuoteAt(state, context.pos)) {
    const word = context.matchBefore(/\w*/);
    return {
      from: word ? word.from : context.pos,
      options: toCompletions(data.colors || [], "constant", "name"),
      validFor: /^\w*$/,
    };
  }

  // 2) 成员访问：xx. 开头
  const memberMatch = context.matchBefore(/([A-Za-z_]\w*)\.\w*/);
  if (memberMatch) {
    const objName = memberMatch.text.split(".")[0];
    const from = memberMatch.from + objName.length + 1;
    const turtleVars = findTurtleVars(text);
    const mods = findImportedModules(text);

    // t. / 自己定义的 turtle 变量名.
    if (objName === "turtle" || objName === "t" || turtleVars.has(objName)) {
      return {
        from,
        options: toCompletions(data.turtleMethods || [], "method", "call").concat(
          toCompletions(data.turtleScreen || [], "function", "call")
        ),
        validFor: /^\w*$/,
      };
    }
    // math. / random. / time. 等
    if (data.modules && data.modules[objName]) {
      return {
        from,
        options: toCompletions(data.modules[objName], "property", "call"),
        validFor: /^\w*$/,
      };
    }
    if (mods.has(objName)) return { from, options: [], validFor: /^\w*$/ };
    return null;
  }

  // 3) 普通标识符
  const word = context.matchBefore(/[\w"]+/);
  if (!word) return null;
  if (word.from === word.to && !context.explicit) return null;

  const options = [];

  // 中文名的代码模板（for循环 / if语句 …）
  (data.snippets || []).forEach((s) => {
    options.push({
      label: s.label,
      detail: s.detail || "",
      desc: s.desc || "",
      type: "text",
      apply: applySnippet(s.snippet || s.label),
      boost: 3,
    });
  });

  // 当前文件里定义过的东西优先
  extractSymbols(text).forEach((s) => {
    options.push({ label: s.label, detail: "", desc: s.detail, type: s.type, boost: 5 });
  });

  // 海龟裸函数（from turtle import * 的情况）
  options.push(...toCompletions(data.turtleMethods || [], "function", "call"));
  options.push(...toCompletions(data.turtleScreen || [], "function", "call"));
  // Python 内置与关键字
  options.push(...toCompletions(data.builtins || [], "function", "call"));
  options.push(...toCompletions(data.keywords || [], "keyword", "name"));

  return {
    from: word.from,
    options: options,
    validFor: /^[\w"]*$/,
  };
}

// ============================================================ 主题

const baseLightTheme = EditorView.theme(
  {
    "&": {
      height: "100%",
      fontSize: "14px",
      backgroundColor: "#ffffff",
      color: "#1f2328",
    },
    ".cm-scroller": {
      fontFamily:
        'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
      lineHeight: "1.65",
    },
    ".cm-content": { padding: "10px 0", caretColor: "#2f6fed" },
    ".cm-gutters": {
      backgroundColor: "#fafbfc",
      color: "#a8b0bc",
      border: "none",
      borderRight: "1px solid #e3e6ec",
    },
    ".cm-activeLineGutter": { backgroundColor: "#eef3fc", color: "#2f6fed" },
    ".cm-activeLine": { backgroundColor: "#f6f9ff" },
    ".cm-selectionBackground, ::selection": { backgroundColor: "#d7e5fd !important" },
    // 整篇全选时整块铺色：首行/末行 CM6 只按文字大小画，末行看着像没选中
    ".cm-all-selected .cm-line, .cm-all-selected .cm-activeLine": { backgroundColor: "#d7e5fd" },
    ".cm-cursor, .cm-dropCursor": { borderLeftColor: "#2f6fed", borderLeftWidth: "2px" },
    // 缩进参考线：在每 4 空格区间靠右画一条浅竖线
    ".cm-indent-guide": {
      backgroundImage:
        "linear-gradient(to right, transparent 0, transparent calc(100% - 1px), #e3e6ec calc(100% - 1px), #e3e6ec 100%)",
    },
    // ---- 自动补全弹窗：尺寸/配色按 codemao 海龟编辑器实测值
    ".cm-tooltip": {
      border: "1px solid #d7dce5",
      borderRadius: "6px",
      boxShadow: "0 6px 20px rgba(16,24,40,.12)",
      backgroundColor: "#fff",
      overflow: "hidden",
    },
    ".cm-tooltip.cm-tooltip-autocomplete": {
      backgroundColor: "#ffffff",
      border: "none",
      borderRadius: "4px",
      boxShadow: "0 0 4px rgba(174,174,174,.8)",
      padding: "0",
      overflow: "visible",
    },
    ".cm-tooltip.cm-tooltip-autocomplete > ul": {
      fontFamily: 'Monaco, Menlo, "Ubuntu Mono", Consolas, "Courier New", monospace',
      fontSize: "14px",
      lineHeight: "26px",
      minWidth: "250px",
      maxWidth: "min(420px, 95vw)",
      maxHeight: "168px",
      padding: "0 0 1px",
      color: "#3d3c3c",
    },
    ".cm-tooltip.cm-tooltip-autocomplete > ul > li": {
      display: "flex",
      flexWrap: "wrap",
      alignItems: "center",
      padding: "2px 3px 0 13px",
      lineHeight: "26px",
      color: "#3d3c3c",
      cursor: "pointer",
    },
    ".cm-tooltip-autocomplete ul li[aria-selected]": {
      backgroundColor: "#fffbf0",
      color: "#3d3c3c",
    },
    ".cm-tooltip-autocomplete > ul > li:hover:not([aria-selected])": {
      backgroundColor: "#f5f6f5",
    },
    ".cm-completionLabel": {
      fontFamily: "inherit",
      whiteSpace: "nowrap",
    },
    ".cm-completionMatchedText": {
      color: "#ff8c1a",
      textDecoration: "none",
    },
    ".cm-completionDetail": {
      marginLeft: "0",
      fontStyle: "normal",
      fontSize: "inherit",
      color: "rgba(0,0,0,.45)",
    },
    ".cm-completionColorBlock": {
      display: "inline-flex",
      width: "15px",
      height: "15px",
      marginLeft: "6px",
      borderRadius: "3px",
      border: "1px solid rgba(0,0,0,.12)",
    },
    // 第二行：中文说明，只在选中/悬停时出现
    ".cm-completionHint": {
      flexBasis: "100%",
      display: "none",
      lineHeight: "1.2",
      padding: "0 0 8px 10px",
      color: "rgba(0,0,0,.4)",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    ".cm-tooltip-autocomplete ul li[aria-selected] .cm-completionHint, .cm-tooltip-autocomplete > ul > li:hover:not([aria-selected]) .cm-completionHint":
      {
        display: "block",
      },
    ".cm-completionInfo": {
      padding: "6px 12px",
      maxWidth: "320px",
      borderRadius: "4px",
      fontFamily:
        '-apple-system, BlinkMacSystemFont, "PingFang SC", "Microsoft YaHei", sans-serif',
      fontSize: "12.5px",
      lineHeight: "1.6",
    },
    ".cm-diagnostic": {
      padding: "4px 8px",
      fontFamily:
        '-apple-system, BlinkMacSystemFont, "PingFang SC", "Microsoft YaHei", sans-serif',
    },
    ".cm-diagnostic-error": { borderLeft: "3px solid #dc2626" },
    ".cm-lintRange-error": {
      backgroundImage: "none",
      textDecoration: "underline wavy #dc2626",
      textUnderlineOffset: "3px",
    },
    ".cm-panels": { backgroundColor: "#fafbfc", color: "#1f2328" },
    ".cm-panels.cm-panels-top": { borderBottom: "1px solid #e3e6ec" },
    ".cm-searchMatch": { backgroundColor: "#fff3b0", outline: "1px solid #e5c100" },
    ".cm-searchMatch.cm-searchMatch-selected": { backgroundColor: "#ffd84d" },
  },
  { dark: false }
);

const darkTheme = EditorView.theme(
  {
    "&": { height: "100%", fontSize: "14px", backgroundColor: "#1e2229", color: "#e6e9ef" },
    ".cm-scroller": {
      fontFamily:
        'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
      lineHeight: "1.65",
    },
    ".cm-content": { padding: "10px 0", caretColor: "#6ea8ff" },
    ".cm-gutters": {
      backgroundColor: "#1a1d23",
      color: "#5c6672",
      border: "none",
      borderRight: "1px solid #2c313a",
    },
    ".cm-activeLineGutter": { backgroundColor: "#242933", color: "#6ea8ff" },
    ".cm-activeLine": { backgroundColor: "#232830" },
    ".cm-selectionBackground, ::selection": { backgroundColor: "#2f4a7a !important" },
    ".cm-all-selected .cm-line, .cm-all-selected .cm-activeLine": { backgroundColor: "#2f4a7a" },
    ".cm-cursor, .cm-dropCursor": { borderLeftColor: "#6ea8ff", borderLeftWidth: "2px" },
    ".cm-indent-guide": {
      backgroundImage:
        "linear-gradient(to right, transparent 0, transparent calc(100% - 1px), #333a45 calc(100% - 1px), #333a45 100%)",
    },
    ".cm-tooltip": {
      border: "1px solid #3a4049",
      borderRadius: "6px",
      backgroundColor: "#232830",
      color: "#e6e9ef",
      overflow: "hidden",
    },
    ".cm-tooltip.cm-tooltip-autocomplete": {
      backgroundColor: "#2e2e2e",
      border: "none",
      borderRadius: "4px",
      boxShadow: "0 0 4px rgba(0,0,0,.55)",
      padding: "0",
      overflow: "visible",
    },
    ".cm-tooltip.cm-tooltip-autocomplete > ul": {
      fontFamily: 'Monaco, Menlo, "Ubuntu Mono", Consolas, "Courier New", monospace',
      fontSize: "14px",
      lineHeight: "26px",
      minWidth: "250px",
      maxWidth: "min(420px, 95vw)",
      maxHeight: "168px",
      padding: "0 0 1px",
      color: "#dbdbdb",
    },
    ".cm-tooltip.cm-tooltip-autocomplete > ul > li": {
      display: "flex",
      flexWrap: "wrap",
      alignItems: "center",
      padding: "2px 3px 0 13px",
      lineHeight: "26px",
      color: "#dbdbdb",
      cursor: "pointer",
    },
    ".cm-tooltip-autocomplete ul li[aria-selected]": {
      backgroundColor: "#3d3c3c",
      color: "#ffffff",
    },
    ".cm-tooltip-autocomplete > ul > li:hover:not([aria-selected])": { backgroundColor: "#333338" },
    ".cm-completionLabel": { fontFamily: "inherit", whiteSpace: "nowrap" },
    ".cm-completionMatchedText": { color: "#ff8c1a", textDecoration: "none" },
    ".cm-completionDetail": {
      marginLeft: "0",
      fontStyle: "normal",
      fontSize: "inherit",
      color: "rgba(255,255,255,.45)",
    },
    ".cm-completionColorBlock": {
      display: "inline-flex",
      width: "15px",
      height: "15px",
      marginLeft: "6px",
      borderRadius: "3px",
      border: "1px solid rgba(255,255,255,.25)",
    },
    ".cm-completionHint": {
      flexBasis: "100%",
      display: "none",
      lineHeight: "1.2",
      padding: "0 0 8px 10px",
      color: "rgba(255,255,255,.4)",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    ".cm-tooltip-autocomplete ul li[aria-selected] .cm-completionHint, .cm-tooltip-autocomplete > ul > li:hover:not([aria-selected]) .cm-completionHint":
      {
        display: "block",
      },
    ".cm-lintRange-error": { textDecoration: "underline wavy #ff6b6b", textUnderlineOffset: "3px" },
    ".cm-panels": { backgroundColor: "#1a1d23", color: "#e6e9ef" },
    ".cm-panels.cm-panels-top": { borderBottom: "1px solid #2c313a" },
  },
  { dark: true }
);

// ============================================================ 创建编辑器

/** 字号主题工厂：Compartment 重配置时用同一份定义，避免自增类名累积。 */
function themeFont(px) {
  return EditorView.theme({ "&": { fontSize: px + "px" } });
}

function create(parent, opts) {
  opts = opts || {};

  const fontCompartment = new Compartment();
  const themeCompartment = new Compartment();
  const lintCompartment = new Compartment();
  const readOnlyCompartment = new Compartment();

  // true 时抑制 onChange/onCursor 回调（切文件、静默设值时用）
  let silent = false;
  // 调试期间临时只读
  let readOnly = !!opts.readOnly;

  let onChange = opts.onChange || function () {};
  let onCursor = opts.onCursor || function () {};
  let checkFn = opts.check || null;
  let fontSize = opts.fontSize || 14;
  let themeName = opts.theme === "dark" ? "dark" : "light";

  // 自定义 linter：把外部异步检查结果转成 CM6 诊断
  // checkFn 允许两种返回形态，避免调用方猜格式：
  //   1) { errors: [{ line, col, length, message }] }   ← /api/check 的原始结构
  //   2) [{ from, to, severity, message }]              ← 已经算好位置的诊断
  const pythonLinter = linter(
    async (view) => {
      if (!checkFn) return [];
      let res;
      try {
        res = await checkFn(view.state.doc.toString());
      } catch (err) {
        return [];
      }
      if (!res) return [];

      // 形态 2：直接是诊断数组
      if (Array.isArray(res)) {
        return res.filter((d) => d && typeof d.from === "number" && typeof d.to === "number");
      }
      if (!res.errors || !res.errors.length) return [];

      const doc = view.state.doc;
      return res.errors.map((e) => {
        const line = doc.line(Math.min(Math.max(e.line || 1, 1), doc.lines));
        const lineLen = line.length;
        // col 是 1 基的列号，可能等于 lineLen+1（错在行尾）
        let start = Math.min(Math.max((e.col || 1) - 1, 0), lineLen);
        let len = Math.max(1, e.length || 1);
        let end = Math.min(lineLen, start + len);
        // ★ 关键：错在行尾时 start 会等于 lineLen，end-start 变 0，
        //   而 CM6 的 mark 装饰必须有长度才会渲染 —— 否则学生看不到任何波浪线。
        //   这种情况下把区间往前挪一个字符，指向"最后一个字符"。
        if (end - start < 1) {
          start = Math.max(0, lineLen - 1);
          end = lineLen;
        }
        // 整行是空的（例如缺缩进的空行）：无法画下划线，交给行号槽图标 + 面板提示
        if (end - start < 1) {
          start = line.from;
          end = Math.min(doc.length, line.from + 1);
        } else {
          start += line.from;
          end += line.from;
        }
        return {
          from: start,
          to: end,
          severity: "error",
          message: e.message || "语法错误",
        };
      });
    },
    { delay: 600 }
  );

  const updateListener = EditorView.updateListener.of((u) => {
    if (silent) return;
    if (u.docChanged) onChange(u.state.doc.toString());
    if (u.docChanged || u.selectionSet) {
      const head = u.state.selection.main.head;
      const line = u.state.doc.lineAt(head);
      onCursor({
        line: line.number,
        col: head - line.from + 1,
        offset: head,
        lines: u.state.doc.lines,
      });
      // 「整篇都被选中」时给编辑器加个标记：
      // CM6 画选区时首行/末行只按文字大小上色（末行更是只盖到文字宽度），
      // 深色主题下最后一行看着像"没选上"。加上这个类，主题里会给整块内容铺一层选区色。
      const sel = u.state.selection.main;
      const whole = u.state.readOnly && u.state.doc.length > 0 &&
        sel.from === 0 && sel.to === u.state.doc.length;
      u.view.scrollDOM.classList.toggle("cm-all-selected", whole);
    }
  });

  // 只读模式下补回「双击选词 / 三击选行」。
  // 只读时编辑器内容仍是 contenteditable，但 CM6 不会把浏览器的多击选区同步进 state，
  // 结果是点两下只停一个光标、点三下也只选一个字，老师会觉得"选不动"。
  // 这里在 mousedown 阶段就自己算选区并吞掉事件，避免浏览器随后又把选区改回去。
  const isWordChar = (ch) => /[A-Za-z0-9_\u4e00-\u9fa5]/.test(ch);
  function spanAtWord(text, i) {
    let s = i;
    let e = i;
    while (s > 0 && isWordChar(text.charAt(s - 1))) s--;
    while (e < text.length && isWordChar(text.charAt(e))) e++;
    return s === e ? [0, text.length] : [s, e];
  }
  const readOnlyNiceSelect = EditorView.domEventHandlers({
    mousedown(event, view) {
      if (!view.state.readOnly || event.detail < 2) return false;
      const pos = view.posAtCoords({ x: event.clientX, y: event.clientY });
      if (pos == null) return false;
      const line = view.state.doc.lineAt(pos);
      let from, to;
      if (event.detail >= 3) {
        from = line.from;
        to = line.to;
      } else {
        const [s, e] = spanAtWord(line.text, pos - line.from);
        from = line.from + s;
        to = line.from + e;
      }
      view.dispatch({ selection: { anchor: from, head: to } });
      view.focus();
      event.preventDefault();
      return true;
    },
  });

  const exts = [
    breakpointGutter(opts.onBreakpointsChange),
    breakpointsField,
    debugLineField,
    edgeDragScroll(),
    readOnlyNiceSelect,
    lineNumbers(),
    highlightActiveLineGutter(),
    highlightSpecialChars(),
    history(),
    foldGutter(),
    drawSelection(),
    dropCursor(),
    EditorState.allowMultipleSelections.of(true),
    indentOnInput(),
    indentUnit.of(" ".repeat(INDENT_SIZE)),
    syntaxHighlighting(defaultHighlightStyle, { fallback: true }),
    bracketMatching(),
    closeBrackets(),
    autocompletion({
      override: [pythonCompletions],
      activateOnTyping: true,
      maxRenderedOptions: 100,
      defaultKeymap: true,
      icons: false,
      // 只按前缀匹配，不要模糊命中（避免 target 里蹦出 shearfactor 这种）
      filterStrict: true,
      // 同分时保留词表顺序，不要被排成字母序
      compareCompletions: () => 0,
      addToOptions: completionRenderers.addToOptions,
    }),
    rectangularSelection(),
    crosshairCursor(),
    highlightActiveLine(),
    highlightSelectionMatches(),
    search({ top: true }),
    indentGuides,
    lintGutter(),
    lintCompartment.of(pythonLinter),
    python(),
    fontCompartment.of(themeFont(opts.fontSize || 14)),
    themeCompartment.of(opts.theme === "dark" ? darkTheme : baseLightTheme),
    updateListener,
    keymap.of([
      ...closeBracketsKeymap,
      ...defaultKeymap,
      ...searchKeymap,
      ...historyKeymap,
      ...foldKeymap,
      ...completionKeymap,
      ...lintKeymap,
      indentWithTab,
      // F8 跳到下一个错误
      {
        key: "F8",
        run: (view) => {
          openLintPanel(view);
          return true;
        },
      },
      // Ctrl/Cmd+Enter 运行
      {
        key: "Mod-Enter",
        run: () => {
          if (opts.onRun) opts.onRun();
          return true;
        },
      },
      // Ctrl/Cmd+S 提交（阻止浏览器保存）
      {
        key: "Mod-s",
        run: () => {
          if (opts.onSubmit) opts.onSubmit();
          return true;
        },
      },
    ]),
    EditorView.contentAttributes.of({
      spellcheck: "false",
      autocorrect: "off",
      autocapitalize: "off",
    }),
  ];

  // 只读模式（教师查看学生代码 / 调试中）：能选中、能复制、能放光标，但改不了内容。
  // 注意别再把 EditorView.editable 设成 false —— 那样 contenteditable 会变 false，
  // 浏览器不再把编辑器当文本区：点一下不出光标、Cmd+A 变成选整个网页、
  // 双击三击选词选行也失灵，选区还会画成整行的色块。只靠 readOnly 拦输入就够了。
  const readOnlyExt = (on) => (on ? [EditorState.readOnly.of(true)] : []);
  exts.push(readOnlyCompartment.of(readOnlyExt(!!opts.readOnly)));

  const state = EditorState.create({ doc: opts.value || "", extensions: exts });

  const view = new EditorView({ state, parent: parent });

  const api = {
    view: view,

    getValue() {
      return view.state.doc.toString();
    },

    setValue(v) {
      view.dispatch({
        changes: { from: 0, to: view.state.doc.length, insert: v || "" },
      });
    },

    /** 静默设值：不触发 onChange / onCursor（切文件时用） */
    setValueSilent(v) {
      silent = true;
      try {
        view.dispatch({
          changes: { from: 0, to: view.state.doc.length, insert: v || "" },
        });
      } finally {
        silent = false;
      }
    },

    focus() {
      view.focus();
    },

    destroy() {
      view.destroy();
    },

    refresh() {
      view.requestMeasure();
    },

    setFontSize(px) {
      fontSize = px;
      view.dispatch({
        effects: fontCompartment.reconfigure(themeFont(px)),
      });
    },

    setTheme(name) {
      themeName = name === "dark" ? "dark" : "light";
      view.dispatch({
        effects: themeCompartment.reconfigure(themeName === "dark" ? darkTheme : baseLightTheme),
      });
    },

    // ---- 多文件支持：整份 State 存取（每个文件各自保留撤销历史与光标）

    /** 取出当前文档的完整 State（可整份存起来）。 */
    getState() {
      return view.state;
    },

    /** 用一份 State 整体替换当前文档（不触发 onChange）。 */
    applyState(st) {
      silent = true;
      try {
        view.setState(st);
      } finally {
        silent = false;
      }
      // 新 State 里的 Compartment 是建它时的旧配置，需按当前设置重新下发
      view.dispatch({
        effects: [
          fontCompartment.reconfigure(themeFont(fontSize)),
          themeCompartment.reconfigure(themeName === "dark" ? darkTheme : baseLightTheme),
          lintCompartment.reconfigure(checkFn ? pythonLinter : []),
          readOnlyCompartment.reconfigure(readOnlyExt(readOnly)),
        ],
      });
    },

    /** 为一段新文本造一份 State（复用全部扩展配置）。 */
    newState(doc) {
      return EditorState.create({ doc: doc || "", extensions: exts });
    },

    // ---- 调试相关：断点槽、当前执行行、调试期间锁编辑

    /** 设置断点（行号数组，1 起） */
    setBreakpoints(lines) {
      view.dispatch({ effects: setBreakpointsEffect.of((lines || []).slice()) });
    },

    /** 取当前断点行号 */
    getBreakpoints() {
      return view.state.field(breakpointsField).lines.slice();
    },

    /** 高亮当前执行行并滚到视野内；传 0 清除 */
    setDebugLine(lineNo) {
      const effects = [setDebugLineEffect.of(lineNo || 0)];
      if (lineNo) {
        const doc = view.state.doc;
        const line = doc.line(Math.min(Math.max(1, lineNo), doc.lines));
        // 只滚动、不动光标：调试时学生要盯着这一行看
        effects.push(EditorView.scrollIntoView(line.from, { y: "center" }));
      }
      view.dispatch({ effects });
    },

    /** 调试中锁住编辑（避免行号和学生看到的代码对不上） */
    setReadOnly(on) {
      readOnly = !!on;
      view.dispatch({ effects: readOnlyCompartment.reconfigure(readOnlyExt(readOnly)) });
    },

    /** 全选代码并把焦点收回来（页面级 Cmd+A 走这里，避免选成整个网页） */
    selectAll() {
      view.focus();
      view.dispatch({
        selection: { anchor: 0, head: view.state.doc.length },
        scrollIntoView: true,
      });
    },

    /** 换掉语法检查函数（可传 null 关闭） */
    setChecker(fn) {
      checkFn = fn;
      view.dispatch({
        effects: lintCompartment.reconfigure(fn ? pythonLinter : []),
      });
    },

    /** 立即跑一次语法检查 */
    runLint() {
      forceLinting(view);
      return true;
    },

    getCursor() {
      const head = view.state.selection.main.head;
      const line = view.state.doc.lineAt(head);
      return { line: line.number, col: head - line.from + 1, offset: head };
    },

    setCursor(offset) {
      const pos = Math.min(Math.max(0, offset), view.state.doc.length);
      view.dispatch({ selection: EditorSelection.cursor(pos), scrollIntoView: true });
      view.focus();
    },

    gotoLine(lineNo) {
      const doc = view.state.doc;
      const line = doc.line(Math.min(Math.max(1, lineNo), doc.lines));
      view.dispatch({ selection: EditorSelection.cursor(line.from), scrollIntoView: true });
      view.focus();
    },
  };

  return api;
}

export { create };
